import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const PaymentInterestCalculator = () => {
  const content = article.find((item) => item.id === 11);
  const [cards, setCards] = useState([
    { 
      id: Date.now(),
      name: "Credit Card 1", 
      balance: "5000", 
      apr: "18.99", 
      minPayment: "", 
      minPaymentPercent: "2", 
      monthlyPayment: "200", 
      extraPayment: "50", 
      promoAPR: "",
      promoEndDate: "",
      fees: "0"
    }
  ]);

  const [paymentFrequency, setPaymentFrequency] = useState("Monthly");
  const [startDate, setStartDate] = useState("");
  const [results, setResults] = useState(null);
  const [totalSavings, setTotalSavings] = useState(0);
  const [showChart, setShowChart] = useState(true);
  const [totalDebt, setTotalDebt] = useState(0);
  const [totalMonthlyPayment, setTotalMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayoffTime, setTotalPayoffTime] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("cards");
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const resultsRef = useRef();

  // Set default start date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  // Calculate totals whenever cards change
  useEffect(() => {
    const debt = cards.reduce((sum, card) => sum + (parseFloat(card.balance) || 0), 0);
    const monthly = cards.reduce((sum, card) => {
      let monthlyPayment = parseFloat(card.monthlyPayment) || 0;
      if (!monthlyPayment && card.minPaymentPercent) {
        monthlyPayment = (parseFloat(card.balance) || 0) * (parseFloat(card.minPaymentPercent) || 0) / 100;
      }
      if (!monthlyPayment) {
        monthlyPayment = parseFloat(card.minPayment) || 0;
      }
      const extra = parseFloat(card.extraPayment) || 0;
      return sum + monthlyPayment + extra;
    }, 0);
    
    setTotalDebt(debt);
    setTotalMonthlyPayment(monthly);
    
    if (cards.every(card => card.balance && card.apr && (card.minPayment || card.minPaymentPercent || card.monthlyPayment))) {
      setTimeout(() => {
        calculatePayoff();
      }, 100);
    }
  }, [cards]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Credit Card ${cards.length + 1}`,
      balance: "3000",
      apr: "22.99",
      minPayment: "",
      minPaymentPercent: "2",
      monthlyPayment: "150",
      extraPayment: "25",
      promoAPR: "",
      promoEndDate: "",
      fees: "0"
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value) => {
    const newCards = cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    );
    setCards(newCards);
  };

  const resetCalculator = () => {
    setCards([
      { 
        id: Date.now(),
        name: "Credit Card 1", 
        balance: "5000", 
        apr: "18.99", 
        minPayment: "", 
        minPaymentPercent: "2", 
        monthlyPayment: "200", 
        extraPayment: "50", 
        promoAPR: "",
        promoEndDate: "",
        fees: "0"
      }
    ]);
    setResults(null);
    setTotalSavings(0);
    setActiveCardIndex(0);
    setStartDate(new Date().toISOString().split('T')[0]);
    setScenarioComparison(null);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  const calculatePayoff = () => {
    const validCards = cards.filter(card => 
      card.balance && card.apr && (card.minPayment || card.minPaymentPercent || card.monthlyPayment)
    );

    if (validCards.length === 0) {
      alert("Please fill in required fields for at least one card (Balance, APR, and at least one payment method)");
      return;
    }

    const payoffResults = validCards.map(card => calculateSingleCardPayoff(card));
    const totalInterestPaid = payoffResults.reduce((sum, res) => sum + (parseFloat(res.totalInterest) || 0), 0);
    const maxPayoffTime = Math.max(...payoffResults.map(res => res.months || 0));
    
    setTotalInterest(totalInterestPaid);
    setTotalPayoffTime(maxPayoffTime);

    // Calculate scenario comparison
    calculateScenarioComparison(validCards);

    setResults(payoffResults);
  };

  const calculateSingleCardPayoff = (card) => {
    const balance = parseFloat(card.balance) || 0;
    const apr = (parseFloat(card.apr) || 0) / 100;
    const fees = parseFloat(card.fees) || 0;
    const extraPayment = parseFloat(card.extraPayment) || 0;
    const promoAPR = card.promoAPR ? (parseFloat(card.promoAPR) || 0) / 100 : null;
    const promoEndDate = card.promoEndDate ? new Date(card.promoEndDate) : null;

    // Determine monthly payment
    let monthlyPayment = parseFloat(card.monthlyPayment) || 0;
    if (!monthlyPayment && card.minPaymentPercent) {
      monthlyPayment = balance * (parseFloat(card.minPaymentPercent) || 0) / 100;
    }
    if (!monthlyPayment) {
      monthlyPayment = parseFloat(card.minPayment) || 0;
    }

    const totalMonthlyPayment = monthlyPayment + extraPayment;

    if (!balance || !totalMonthlyPayment || (!apr && !promoAPR)) return null;

    let remaining = balance;
    let months = 0;
    let totalInterest = 0;
    const monthlyData = [];
    const interestData = [];
    const monthlyBreakdown = [];

    while (remaining > 0 && months < 600) {
      // Check if promo APR is still active
      const currentAPR = promoAPR && (!promoEndDate || months < 1) ? promoAPR : apr;
      
      const interest = remaining * (currentAPR / 12);
      totalInterest += interest;
      const principalPayment = Math.min(totalMonthlyPayment - interest, remaining);
      remaining = remaining - principalPayment + fees;
      
      if (remaining < 0) remaining = 0;
      
      months++;
      monthlyData.push(remaining > 0 ? remaining : 0);
      interestData.push(interest);
      
      monthlyBreakdown.push({
        month: months,
        interest: interest.toFixed(2),
        principal: principalPayment.toFixed(2),
        fees: fees.toFixed(2),
        remaining: remaining.toFixed(2),
        apr: (currentAPR * 100).toFixed(2)
      });
      
      if (remaining <= 0) break;
    }

    // Debt-free date calculation
    const start = startDate ? new Date(startDate) : new Date();
    const debtFreeDate = new Date(start);
    debtFreeDate.setMonth(debtFreeDate.getMonth() + months);

    return {
      name: card.name,
      months,
      totalInterest: totalInterest.toFixed(2),
      totalPayment: (balance + totalInterest).toFixed(2),
      debtFreeDate: debtFreeDate.toLocaleDateString(),
      monthlyData,
      interestData,
      monthlyBreakdown,
      originalPayoff: months
    };
  };

  const calculateScenarioComparison = (validCards) => {
    const scenarios = [
      { name: "Current Plan", paymentMultiplier: 1 },
      { name: "Pay 10% More", paymentMultiplier: 1.1 },
      { name: "Pay 25% More", paymentMultiplier: 1.25 },
      { name: "Double Payments", paymentMultiplier: 2 }
    ];

    const comparison = scenarios.map(scenario => {
      let totalMonths = 0;
      let totalInterest = 0;

      validCards.forEach(card => {
        const balance = parseFloat(card.balance) || 0;
        const apr = (parseFloat(card.apr) || 0) / 100;
        let monthlyPayment = parseFloat(card.monthlyPayment) || 0;
        
        if (!monthlyPayment && card.minPaymentPercent) {
          monthlyPayment = balance * (parseFloat(card.minPaymentPercent) || 0) / 100;
        }
        if (!monthlyPayment) {
          monthlyPayment = parseFloat(card.minPayment) || 0;
        }

        const extraPayment = parseFloat(card.extraPayment) || 0;
        const totalPayment = (monthlyPayment + extraPayment) * scenario.paymentMultiplier;

        if (!balance || !totalPayment || !apr) return;

        let remaining = balance;
        let months = 0;
        let cardInterest = 0;

        while (remaining > 0 && months < 600) {
          const interest = remaining * (apr / 12);
          cardInterest += interest;
          const principalPayment = Math.min(totalPayment - interest, remaining);
          remaining -= principalPayment;
          months++;
          if (remaining <= 0) break;
        }

        totalMonths = Math.max(totalMonths, months);
        totalInterest += cardInterest;
      });

      return {
        name: scenario.name,
        months: totalMonths,
        totalInterest: totalInterest.toFixed(2),
        paymentMultiplier: scenario.paymentMultiplier
      };
    });

    setScenarioComparison(comparison);
  };

  // Chart data configurations
  const payoffChartData = {
    labels: results?.map(res => res?.name).filter(Boolean) || [],
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: results?.map(res => res?.months).filter(Boolean) || [],
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
      },
    ],
  };

  const interestVsPrincipalData = {
    labels: results?.map(res => res?.name).filter(Boolean) || [],
    datasets: [
      {
        label: 'Principal Balance',
        data: cards.map(card => parseFloat(card.balance) || 0),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Total Interest',
        data: results ? cards.map((card, index) => parseFloat(results[index]?.totalInterest || 0)) : Array(cards.length).fill(0),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  };

  const scenarioComparisonData = {
    labels: scenarioComparison?.map(scenario => scenario.name) || [],
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: scenarioComparison?.map(scenario => scenario.months) || [],
        backgroundColor: '#FF8C42',
        borderColor: '#FF8C42',
        borderWidth: 1,
      },
      {
        label: 'Total Interest ($)',
        data: scenarioComparison?.map(scenario => parseFloat(scenario.totalInterest)) || [],
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
        yAxisID: 'y1',
      }
    ],
  };

  const balanceOverTimeData = (cardIndex) => {
    const result = results?.[cardIndex];
    if (!result) return null;

    return {
      labels: Array.from({ length: result.months }, (_, i) => `Month ${i + 1}`),
      datasets: [
        {
          label: 'Remaining Balance',
          data: result.monthlyData,
          borderColor: '#F06201',
          backgroundColor: 'rgba(240, 98, 1, 0.1)',
          borderWidth: 2,
          fill: true,
        },
        {
          label: 'Interest Paid',
          data: result.interestData,
          borderColor: '#A03B00',
          backgroundColor: 'rgba(160, 59, 0, 0.1)',
          borderWidth: 2,
          fill: true,
        }
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const scenarioChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Months'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Interest ($)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Payment & Interest Calculator</h1>
          <p>Analyze interest costs and optimize your payment strategy</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Debt:</span>
              <strong>${totalDebt.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Monthly Payment:</span>
              <strong>${totalMonthlyPayment.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Interest:</span>
              <strong>${totalInterest.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Payoff Time:</span>
              <strong>{totalPayoffTime} months</strong>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'cards' ? 'active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <i className="fa-solid fa-credit-card"></i>
          Cards
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Credit Cards */}
        <div className={`left-panel ${activeTab === 'cards' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Your Credit Cards</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  + Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-calculator"></i>
                {" "}Calculate exact interest costs and analyze different payment scenarios
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Current Balance ($)*</label>
                      <input
                        type="number"
                        value={card.balance}
                        onChange={(e) => handleCardChange(card.id, "balance", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>APR (%)*</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.apr}
                        onChange={(e) => handleCardChange(card.id, "apr", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Minimum Payment ($)</label>
                      <input
                        type="number"
                        value={card.minPayment}
                        onChange={(e) => handleCardChange(card.id, "minPayment", e.target.value)}
                        placeholder="Fixed amount"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Minimum Payment (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.minPaymentPercent}
                        onChange={(e) => handleCardChange(card.id, "minPaymentPercent", e.target.value)}
                        placeholder="Percentage"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Monthly Payment ($)</label>
                      <input
                        type="number"
                        value={card.monthlyPayment}
                        onChange={(e) => handleCardChange(card.id, "monthlyPayment", e.target.value)}
                        placeholder="Your planned payment"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Extra Payments ($)</label>
                      <input
                        type="number"
                        value={card.extraPayment}
                        onChange={(e) => handleCardChange(card.id, "extraPayment", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Promotional APR (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.promoAPR}
                        onChange={(e) => handleCardChange(card.id, "promoAPR", e.target.value)}
                        placeholder="Optional"
                        min="0"
                        max="100"
                      />
                    </div>

                    <div className="input-group">
                      <label>Promo End Date</label>
                      <input
                        type="date"
                        value={card.promoEndDate}
                        onChange={(e) => handleCardChange(card.id, "promoEndDate", e.target.value)}
                      />
                    </div>

                    <div className="input-group">
                      <label>Monthly Fees ($)</label>
                      <input
                        type="number"
                        value={card.fees}
                        onChange={(e) => handleCardChange(card.id, "fees", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="required-note">* Required fields. Fill at least one payment method.</div>
                </div>
              ))}
            </div>
          </div>

          <div className="payment-settings">
            <div className="section-header">
              <h2>Calculation Settings</h2>
            </div>
            <div className="settings-grid">
              <div className="input-group">
                <label>Payment Frequency</label>
                <select 
                  value={paymentFrequency} 
                  onChange={(e) => setPaymentFrequency(e.target.value)}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-weekly">Bi-weekly</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>

              <div className="input-group">
                <label>Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </div>
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculatePayoff}>
              Calculate Interest & Payments
            </button>
          </div>

           <div className="default-results">
              <h2>Interest Analysis Results</h2>
              <div className="default-message">
                <p>Fill in your credit card information and click "Calculate Interest & Payments" to see detailed interest costs and payment scenarios.</p>
                <div className="default-tips">
                  <h3><i className="fa-solid fa-lightbulb"></i> Interest Saving Strategies:</h3>
                  <ul>
                    <li>Even small increases in monthly payments can significantly reduce total interest</li>
                    <li>Pay more during promotional APR periods to maximize principal reduction</li>
                    <li>Consider balance transfers to lower APR cards when fees make sense</li>
                    <li>Make payments more frequently than monthly to reduce average daily balance</li>
                    <li>Always pay at least the minimum to avoid late fees and penalty APRs</li>
                  </ul>
                </div>
              </div>
            </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Interest Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Total Payoff Time</h3>
                  <div className="summary-value">{totalPayoffTime} months</div>
                  <div className="summary-note">Until all cards are paid off</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Total Interest</h3>
                  <div className="summary-value">${totalInterest.toFixed(2)}</div>
                  <div className="summary-note">Interest you'll pay</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Interest Rate</h3>
                  <div className="summary-value">
                    {cards.length > 0 ? (cards.reduce((sum, card) => sum + parseFloat(card.apr || 0), 0) / cards.length).toFixed(2) : '0.00'}%
                  </div>
                  <div className="summary-note">Average APR across all cards</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-credit-card"></i></div>
                  <h3>Total Debt</h3>
                  <div className="summary-value">${totalDebt.toFixed(2)}</div>
                  <div className="summary-note">Combined balance from all cards</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Payoff Timeline Comparison</h3>
                    <Bar data={payoffChartData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Payoff Timeline:</strong> Shows how many months each credit card will take to pay off completely based on your current payments.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Principal vs Interest</h3>
                    <Bar data={interestVsPrincipalData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Principal vs Interest:</strong> Compares your original debt balance with the total interest you'll pay over time.</p>
                    </div>
                  </div>

                  {scenarioComparison && (
                    <div className="chart-card">
                      <h3>Payment Scenario Comparison</h3>
                      <Bar data={scenarioComparisonData} options={scenarioChartOptions} />
                      <div className="chart-description">
                        <p><strong>Scenario Analysis:</strong> Compare how increasing your payments affects payoff time and total interest costs.</p>
                      </div>
                    </div>
                  )}

                  {results.map((result, index) => (
                    balanceOverTimeData(index) && (
                      <div key={index} className="chart-card">
                        <h3>{result.name} - Balance Over Time</h3>
                        <Line data={balanceOverTimeData(index)} options={chartOptions} />
                        <div className="chart-description">
                          <p><strong>Balance Progression:</strong> Track how your balance decreases and interest accumulates each month.</p>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((res, idx) =>
                      res ? (
                        <div key={idx} className="result-card">
                          <h3>{res.name}</h3>
                          <div className="result-stats">
                            <div className="stat">
                              <label>Payoff Time</label>
                              <span className="stat-value">{res.months} months</span>
                            </div>
                            <div className="stat">
                              <label>Total Interest</label>
                              <span className="stat-value">${res.totalInterest}</span>
                            </div>
                            <div className="stat">
                              <label>Total Payment</label>
                              <span className="stat-value">${res.totalPayment}</span>
                            </div>
                            <div className="stat">
                              <label>Debt-Free Date</label>
                              <span className="stat-value">{res.debtFreeDate}</span>
                            </div>
                            <div className="stat">
                              <label>Monthly Payment</label>
                              <span className="stat-value">
                                ${(parseFloat(cards[idx].monthlyPayment) || 
                                  (cards[idx].minPaymentPercent ? 
                                    (parseFloat(cards[idx].balance) * parseFloat(cards[idx].minPaymentPercent) / 100) : 
                                    parseFloat(cards[idx].minPayment) || 0) + 
                                  parseFloat(cards[idx].extraPayment || 0)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="monthly-breakdown">
                            <h4>Monthly Payment Breakdown</h4>
                            <div className="breakdown-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Month</th>
                                    <th>Interest</th>
                                    <th>Principal</th>
                                    <th>Fees</th>
                                    <th>APR</th>
                                    <th>Remaining</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {res.monthlyBreakdown.slice(0, 12).map((month, i) => (
                                    <tr key={i}>
                                      <td>{month.month}</td>
                                      <td>${month.interest}</td>
                                      <td>${month.principal}</td>
                                      <td>${month.fees}</td>
                                      <td>{month.apr}%</td>
                                      <td>${month.remaining}</td>
                                    </tr>
                                  ))}
                                  {res.monthlyBreakdown.length > 12 && (
                                    <tr>
                                      <td colSpan="6" style={{textAlign: 'center', fontStyle: 'italic'}}>
                                        ... and {res.monthlyBreakdown.length - 12} more months
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div key={idx} className="result-card error">
                          <p>Incomplete data for {cards[idx].name}</p>
                        </div>
                      )
                    )}
                  </div>

                  {scenarioComparison && (
                    <div className="scenario-comparison">
                      <h3>Payment Scenario Analysis</h3>
                      <div className="scenario-grid">
                        {scenarioComparison.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Payoff Time</label>
                                <span>{scenario.months} months</span>
                              </div>
                              <div className="stat">
                                <label>Total Interest</label>
                                <span>${scenario.totalInterest}</span>
                              </div>
                              <div className="stat">
                                <label>Time Saved</label>
                                <span>{scenarioComparison[0].months - scenario.months} months</span>
                              </div>
                              <div className="stat">
                                <label>Interest Saved</label>
                                <span>${(scenarioComparison[0].totalInterest - scenario.totalInterest).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your details and click "Calculate Interest" to view results.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default PaymentInterestCalculator;